import React, { Suspense, lazy, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getProfile, logout } from './store/authSlice'
import { Box, CircularProgress } from '@mui/material'

const MainLayout = lazy(() => import('./components/Layout/MainLayout'))
const Login = lazy(() => import('./pages/Auth/Login'))
const Register = lazy(() => import('./pages/Auth/Register'))
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword'))
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'))
const Chat = lazy(() => import('./pages/Chat/Chat'))
const Matches = lazy(() => import('./pages/Matches/Matches'))
const Connections = lazy(() => import('./pages/Connections/Connections'))
const Settings = lazy(() => import('./pages/Settings/Settings'))
const Admin = lazy(() => import('./pages/Admin/Admin'))

const LoadingScreen = () => (
  <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
    <CircularProgress />
  </Box>
)

function App() {
  const dispatch = useDispatch()
  const { isAuthenticated, user } = useSelector((state) => state.auth)

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getProfile())
    }
  }, [dispatch, isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated || !user?.isGuest || !user?.guestExpiresAt) return undefined
    const expiresAt = new Date(`${user.guestExpiresAt}Z`).getTime()
    const delay = expiresAt - Date.now()
    if (!Number.isFinite(delay) || delay <= 0) {
      dispatch(logout())
      return undefined
    }
    const timer = window.setTimeout(() => dispatch(logout()), delay)
    return () => window.clearTimeout(timer)
  }, [dispatch, isAuthenticated, user?.guestExpiresAt, user?.isGuest])

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <Login />} />
        <Route path="/register" element={isAuthenticated ? <Navigate to="/" /> : <Register />} />
        <Route path="/forgot-password" element={isAuthenticated ? <Navigate to="/" /> : <ForgotPassword />} />
        
        <Route path="/" element={isAuthenticated ? <MainLayout /> : <Navigate to="/login" />}>
          <Route index element={<Dashboard />} />
          <Route path="chat" element={<Chat />} />
          <Route path="matches" element={<Matches />} />
          <Route path="connections" element={<Connections />} />
          <Route path="settings" element={<Settings />} />
          <Route path="admin" element={user?.isAdmin ? <Admin /> : <Navigate to="/" replace />} />
        </Route>
        
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Suspense>
  )
}

export default App
